/**
 * Landing Page V2 - Nexgo Journeys Section
 * Merges signature journeys and curated packages into one section.
 */

'use client';

import React from 'react';
import {
  typographyColors,
  backgroundColors,
  strokeColors,
  spacing,
  typography,
  fontPrimary,
} from '@/design-system/utils';
import Image from 'next/image';
import Link from 'next/link';
import { useCuratedPackages, useSignatureJourneys } from '@/hooks/useExperiences';
import type { Experience } from '@/services/experienceService';
import { getExperienceImage } from '@/services/experienceService';
import { ScrollReveal } from './ScrollReveal';
import { useCurrency } from '@/hooks/useCurrency';
import { useConvertedPrice } from '@/hooks/useCurrencyPrice';
import { getCurrencyCode } from '@/utils/currencyHelpers';

// Fallback packages if API fails (not used - sections hide if no API data)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const fallbackPackages: Experience[] = [
  {
    _id: 'pkg-1',
    name: 'Peru Package',
    title: 'Peru In 7 Days: Machu Picchu And Sacred Valley',
    slug: 'peru-7-days',
    code: 'PKG-001',
    type: 'package',
    category: {
      title: 'International Package',
      description: 'Multi-day travel package',
      _id: 'cat-pkg-1',
    },
    location: {
      _id: 'loc-pkg-1',
      name: 'Machu Picchu',
      state: 'Cusco',
      city: 'Cusco',
    },
    imageUrl: ['/assets/images/location/peru-1.png'],
    duration: { days: 5, nights: 4 },
    price: 23000,
    currencySymbol: '₹',
    averageRating: 4.8,
    reviewCount: 120,
    isActive: true,
    curated: false,
    createdAt: new Date().toISOString(),
  },
  {
    _id: 'pkg-2',
    name: 'Nepal Package',
    title: 'Nepal In 7 Days: Kathmandu Heritage & Himalayan Views',
    slug: 'nepal-7-days',
    code: 'PKG-002',
    type: 'package',
    category: {
      title: 'International Package',
      description: 'Multi-day travel package',
      _id: 'cat-pkg-2',
    },
    location: {
      _id: 'loc-pkg-2',
      name: 'Nepal',
      state: 'Bagmati',
      city: 'Kathmandu',
    },
    imageUrl: ['/assets/images/location/nepal-1.png'],
    duration: { days: 7, nights: 6 },
    price: 33000,
    currencySymbol: '₹',
    averageRating: 4.9,
    reviewCount: 150,
    isActive: true,
    curated: false,
    createdAt: new Date().toISOString(),
  },
  {
    _id: 'pkg-3',
    name: 'Sri Lanka Package',
    title: 'Sri Lanka In 8 Days: Sigiriya Rock, Tea Hills & Coastal Temples',
    slug: 'sri-lanka-8-days',
    code: 'PKG-003',
    type: 'package',
    category: {
      title: 'International Package',
      description: 'Multi-day travel package',
      _id: 'cat-pkg-3',
    },
    location: {
      _id: 'loc-pkg-3',
      name: 'Sri Lanka',
      state: 'Central Province',
      city: 'Sigiriya',
    },
    imageUrl: ['/assets/images/packages/image-1.png'],
    duration: { days: 5, nights: 4 },
    price: 40000,
    currencySymbol: '₹',
    averageRating: 4.8,
    reviewCount: 98,
    isActive: true,
    curated: false,
    createdAt: new Date().toISOString(),
  },
  {
    _id: 'pkg-4',
    name: 'Thailand Package',
    title: 'Thailand In 6 Days: Bangkok & Phuket Adventure',
    slug: 'thailand-6-days',
    code: 'PKG-004',
    type: 'package',
    category: {
      title: 'International Package',
      description: 'Multi-day travel package',
      _id: 'cat-pkg-4',
    },
    location: {
      _id: 'loc-pkg-4',
      name: 'Thailand',
      state: 'Bangkok',
      city: 'Bangkok',
    },
    imageUrl: ['/assets/images/packages/image-2.png'],
    duration: { days: 6, nights: 5 },
    price: 35000,
    currencySymbol: '₹',
    averageRating: 4.7,
    reviewCount: 85,
    isActive: true,
    curated: false,
    createdAt: new Date().toISOString(),
  },
  {
    _id: 'pkg-5',
    name: 'Bali Package',
    title: 'Bali In 5 Days: Beaches, Temples & Rice Terraces',
    slug: 'bali-5-days',
    code: 'PKG-005',
    type: 'package',
    category: {
      title: 'International Package',
      description: 'Multi-day travel package',
      _id: 'cat-pkg-5',
    },
    location: {
      _id: 'loc-pkg-5',
      name: 'Bali',
      state: 'Bali',
      city: 'Ubud',
    },
    imageUrl: ['/assets/images/packages/image-3.png'],
    duration: { days: 5, nights: 4 },
    price: 28000,
    currencySymbol: '₹',
    averageRating: 4.9,
    reviewCount: 110,
    isActive: true,
    curated: false,
    createdAt: new Date().toISOString(),
  },
  {
    _id: 'pkg-6',
    name: 'Vietnam Package',
    title: 'Vietnam In 8 Days: Hanoi, Ha Long Bay & Ho Chi Minh',
    slug: 'vietnam-8-days',
    code: 'PKG-006',
    type: 'package',
    category: {
      title: 'International Package',
      description: 'Multi-day travel package',
      _id: 'cat-pkg-6',
    },
    location: {
      _id: 'loc-pkg-6',
      name: 'Vietnam',
      state: 'Hanoi',
      city: 'Hanoi',
    },
    imageUrl: ['/assets/images/packages/image-4.png'],
    duration: { days: 8, nights: 7 },
    price: 38000,
    currencySymbol: '₹',
    averageRating: 4.8,
    reviewCount: 92,
    isActive: true,
    curated: false,
    createdAt: new Date().toISOString(),
  },
  {
    _id: 'pkg-7',
    name: 'Dubai Package',
    title: 'Dubai In 4 Days: Luxury & Desert Safari',
    slug: 'dubai-4-days',
    code: 'PKG-007',
    type: 'package',
    category: {
      title: 'International Package',
      description: 'Multi-day travel package',
      _id: 'cat-pkg-7',
    },
    location: {
      _id: 'loc-pkg-7',
      name: 'Dubai',
      state: 'Dubai',
      city: 'Dubai',
    },
    imageUrl: ['/assets/images/packages/image-5.png'],
    duration: { days: 4, nights: 3 },
    price: 45000,
    currencySymbol: '₹',
    averageRating: 4.6,
    reviewCount: 78,
    isActive: true,
    curated: false,
    createdAt: new Date().toISOString(),
  },
  {
    _id: 'pkg-8',
    name: 'Maldives Package',
    title: 'Maldives In 5 Days: Beach Paradise & Water Sports',
    slug: 'maldives-5-days',
    code: 'PKG-008',
    type: 'package',
    category: {
      title: 'International Package',
      description: 'Multi-day travel package',
      _id: 'cat-pkg-8',
    },
    location: {
      _id: 'loc-pkg-8',
      name: 'Maldives',
      state: 'Male',
      city: 'Male',
    },
    imageUrl: ['/assets/images/packages/image-6.png'],
    duration: { days: 5, nights: 4 },
    price: 55000,
    currencySymbol: '₹',
    averageRating: 5.0,
    reviewCount: 125,
    isActive: true,
    curated: false,
    createdAt: new Date().toISOString(),
  },
  {
    _id: 'pkg-9',
    name: 'Singapore Package',
    title: 'Singapore In 4 Days: Gardens & Marina Bay',
    slug: 'singapore-4-days',
    code: 'PKG-009',
    type: 'package',
    category: {
      title: 'International Package',
      description: 'Multi-day travel package',
      _id: 'cat-pkg-9',
    },
    location: {
      _id: 'loc-pkg-9',
      name: 'Singapore',
      state: 'Singapore',
      city: 'Singapore',
    },
    imageUrl: ['/assets/images/packages/image-7.png'],
    duration: { days: 4, nights: 3 },
    price: 42000,
    currencySymbol: '₹',
    averageRating: 4.7,
    reviewCount: 88,
    isActive: true,
    curated: false,
    createdAt: new Date().toISOString(),
  },
  {
    _id: 'pkg-10',
    name: 'Malaysia Package',
    title: 'Malaysia In 6 Days: Kuala Lumpur & Langkawi',
    slug: 'malaysia-6-days',
    code: 'PKG-010',
    type: 'package',
    category: {
      title: 'International Package',
      description: 'Multi-day travel package',
      _id: 'cat-pkg-10',
    },
    location: {
      _id: 'loc-pkg-10',
      name: 'Malaysia',
      state: 'Kuala Lumpur',
      city: 'Kuala Lumpur',
    },
    imageUrl: ['/assets/images/packages/image-8.png'],
    duration: { days: 6, nights: 5 },
    price: 32000,
    currencySymbol: '₹',
    averageRating: 4.6,
    reviewCount: 95,
    isActive: true,
    curated: false,
    createdAt: new Date().toISOString(),
  },
];

function mergeJourneys(
  signatureJourneys: Experience[] = [],
  curatedPackages: Experience[] = []
): Experience[] {
  const seen = new Set<string>();
  const merged: Experience[] = [];
  for (const item of [...signatureJourneys, ...curatedPackages]) {
    if (!seen.has(item._id)) {
      seen.add(item._id);
      merged.push(item);
    }
  }
  return merged;
}

export function NexgoJourneysSection() {
  const {
    data: signatureData,
    isLoading: isLoadingSignature,
    error: signatureError,
  } = useSignatureJourneys();
  const {
    data: curatedData,
    isLoading: isLoadingCurated,
    error: curatedError,
  } = useCuratedPackages();

  const isLoading = isLoadingSignature || isLoadingCurated;
  const error = signatureError || curatedError;
  const data = React.useMemo(
    () => mergeJourneys(signatureData, curatedData),
    [signatureData, curatedData]
  );

  // Ref for scrollable container
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const isDraggingRef = React.useRef(false);
  const startXRef = React.useRef(0);
  const scrollLeftRef = React.useRef(0);
  const hasDraggedRef = React.useRef(false);
  const shouldPreventClickRef = React.useRef(false);

  // Debug logging
  React.useEffect(() => {
    console.log('=== NexgoJourneys Debug ===');
    console.log('isLoading:', isLoading);
    console.log('data:', data);
    console.log('data length:', data?.length);
    console.log('error:', error);
    console.log('=============================');
  }, [data, isLoading, error]);

  // Only use API data, don't show fallback
  // Hide section if no API data
  const hasApiData = data && Array.isArray(data) && data.length > 0;
  const allJourneys = hasApiData ? data : [];
  const packages = allJourneys.slice(0, 3); // Limit to 3 items per Figma design

  // Enable mouse drag scrolling
  React.useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || isLoading) return;

    const handleMouseDown = (e: MouseEvent) => {
      // Check if container has horizontal overflow
      const hasHorizontalScroll = container.scrollWidth > container.clientWidth;
      if (!hasHorizontalScroll) {
        return;
      }

      // Allow dragging even on links, but we'll prevent navigation if dragged
      isDraggingRef.current = true;
      hasDraggedRef.current = false;
      const rect = container.getBoundingClientRect();
      startXRef.current = e.clientX - rect.left;
      scrollLeftRef.current = container.scrollLeft;

      container.style.cursor = 'grabbing';
      container.style.userSelect = 'none';

      // Prevent default to avoid text selection and link navigation
      e.preventDefault();
      e.stopPropagation();
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current) return;

      e.preventDefault();
      e.stopPropagation();

      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const walk = x - startXRef.current; // Distance moved

      // Only mark as dragged if movement is significant (more than 5px)
      if (Math.abs(walk) > 5) {
        hasDraggedRef.current = true;
        shouldPreventClickRef.current = true;
      }

      // Update scroll position
      // Dragging right (positive walk) should scroll right (increase scrollLeft)
      // Dragging left (negative walk) should scroll left (decrease scrollLeft)
      const newScrollLeft = scrollLeftRef.current - walk;
      container.scrollLeft = newScrollLeft;
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;

      if (container) {
        container.style.cursor = 'grab';
        container.style.userSelect = '';
      }

      // Reset hasDragged after a short delay to allow click handler to check it
      if (hasDraggedRef.current) {
        // Keep the flag for a bit to prevent click
        setTimeout(() => {
          hasDraggedRef.current = false;
          shouldPreventClickRef.current = false;
        }, 200);
      } else {
        // No drag occurred, allow click
        shouldPreventClickRef.current = false;
        hasDraggedRef.current = false;
      }
    };

    const handleMouseLeave = () => {
      isDraggingRef.current = false;
      hasDraggedRef.current = false;
      shouldPreventClickRef.current = false;
      if (container) {
        container.style.cursor = 'grab';
        container.style.userSelect = '';
      }
    };

    const handleClick = (e: MouseEvent) => {
      // Prevent navigation if we dragged
      if (shouldPreventClickRef.current || hasDraggedRef.current) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return false;
      }
    };

    container.addEventListener('mousedown', handleMouseDown, {
      passive: false,
    });
    document.addEventListener('mousemove', handleMouseMove, { passive: false });
    document.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mouseleave', handleMouseLeave);
    // Use capture phase to intercept clicks before they reach links
    container.addEventListener('click', handleClick, { capture: true });

    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      container.removeEventListener('mouseleave', handleMouseLeave);
      container.removeEventListener('click', handleClick, { capture: true });
    };
  }, [isLoading, packages.length]);

  console.log('NexgoJourneys - Final journeys count:', packages.length);
  console.log(
    'NexgoJourneys - Using:',
    data && data.length > 0 ? 'API DATA' : 'FALLBACK'
  );

  // Hide section if no API data
  if (!hasApiData && !isLoading) {
    return null;
  }

  return (
    <ScrollReveal
      as='section'
      className='px-4 py-8 md:px-20 md:py-20'
      style={{}}
      direction='up'
      amount={0.25}
      margin='0px 0px -120px 0px'
    >
      <ScrollReveal
        className='max-w-[1407px] mx-auto'
        delay={0.1}
        direction='none'
      >
        {/* Header */}
        <ScrollReveal
          className='flex flex-col items-center w-full md:w-[556px] mx-auto'
          style={{
            gap: spacing[2], // 8px
            marginBottom: spacing[8], // 32px
          }}
          delay={0.2}
          direction='up'
        >
          <div
            className='flex items-center justify-center shrink-0'
            style={{
              gap: '10px',
              paddingLeft: spacing[1],
              paddingRight: spacing[3],
              paddingTop: spacing[1],
              paddingBottom: spacing[1],
            }}
          >
            <div
              className='flex items-center justify-center shrink-0 border border-black/12'
              style={{
                backgroundColor: '#fde047',
                padding: spacing[2],
                borderRadius: '9999px',
                width: '36px',
                height: '36px',
              }}
            >
              <Image
                src='/assets/images/icons/landing-page/curated_packages.svg'
                alt='Nexgo Journeys'
                width={20}
                height={20}
                unoptimized
              />
            </div>
            <p
              className='shrink-0'
              style={{
                fontFamily: fontPrimary,
                fontWeight: 700,
                fontSize: '16px',
                lineHeight: 'normal',
                color: typographyColors.primary,
              }}
            >
              Nexgo Journeys
            </p>
          </div>

          <h2
            className='
    text-center whitespace-pre-wrap
    text-[24px] leading-[32px]
    md:text-[48px] md:leading-[52px]
  '
            style={{
              fontFamily: fontPrimary,
              fontWeight: typography.weight.semibold, // 600
              color: typographyColors.primary,
              minWidth: '100%',
            }}
          >
            Thoughtfully curated journeys for meaningful and refined exploration
          </h2>

          <p
            className='text-center whitespace-pre-wrap text-sm md:text-xl'
            style={{
              fontFamily: fontPrimary,
              fontWeight: typography.weight.regular, // 400
              lineHeight: 'normal',
              color: typographyColors.primary,
              minWidth: '100%',
            }}
          >
            For the Curious Traveller
          </p>
        </ScrollReveal>

        {/* Package Cards - Fixed 3-card layout */}
        <div
          style={{
            marginTop: spacing[8], // 32px gap from header
            marginBottom: spacing[8], // 32px gap to explore link
          }}
        >
          <div
            className='flex flex-col md:flex-row'
            style={{
              gap: spacing[6], // 24px gap between cards from Figma
              width: '100%',
            }}
          >
            {isLoading ? (
              // Loading skeleton - 3 items
              <>
                {[1, 2, 3].map(i => (
                  <div
                    key={i}
                    className='flex-1 animate-pulse rounded-xl overflow-hidden'
                    style={{
                      minWidth: '280px',
                      height: '436px',
                      backgroundColor: backgroundColors.secondary,
                    }}
                  />
                ))}
              </>
            ) : (
              packages.map(pkg => (
                <div
                  key={pkg._id}
                  className='flex-1'
                  style={{ minWidth: '280px' }}
                >
                  <PackageCard experience={pkg} />
                </div>
              ))
            )}
          </div>
        </div>

        {/* Explore Link */}
        <ScrollReveal
          className='flex items-center justify-center'
          style={{ gap: spacing[1.5] }} // 6px
          delay={0.45}
          direction='none'
        >
          <Link
            href='/experiences?type=nexgo-journeys'
            className='cursor-pointer hover:opacity-80 transition-opacity underline'
            style={{
              fontFamily: fontPrimary,
              fontWeight: 700,
              fontSize: '16px',
              lineHeight: 'normal',
              color: typographyColors.primary,
              textUnderlinePosition: 'from-font',
              textDecorationLine: 'underline',
            }}
          >
            Explore All Nexgo Journeys
          </Link>
          <svg
            width='20'
            height='20'
            viewBox='0 0 20 20'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              fillRule='evenodd'
              d='M5.22 14.78a.75.75 0 001.06 0l7.22-7.22v5.69a.75.75 0 001.5 0v-7.5a.75.75 0 00-.75-.75h-7.5a.75.75 0 000 1.5h5.69l-7.22 7.22a.75.75 0 000 1.06z'
              clipRule='evenodd'
              fill='#27272a'
            />
          </svg>
        </ScrollReveal>
      </ScrollReveal>
    </ScrollReveal>
  );
}

interface PackageCardProps {
  experience: Experience;
}

function PackageCard({ experience }: PackageCardProps) {
  const {
    title,
    location,
    shortTitle,
    name,
    price,
    currencySymbol,
    duration,
    slug,
  } = experience;

  // Get image from media array, with fallback to imageUrl
  const displayImage = getExperienceImage(experience);

  // Use location name if available
  const displayLocation =
    location?.city || location?.name || location?.state || 'Location TBD';

  // Use shortTitle if available, otherwise title or name
  const displayTitle = shortTitle || title || name;

  // Infer backend currency from currencySymbol (₹ = INR, $ = USD, etc.)
  // Defaults to USD if currencySymbol doesn't match known symbols
  const getBackendCurrency = (symbol?: string | null): string => {
    if (!symbol) return 'USD';
    if (symbol === '₹' || symbol === 'INR') return 'INR';
    if (symbol === '$' || symbol === 'USD') return 'USD';
    // Default to USD for unknown symbols
    return 'USD';
  };

  const backendCurrency = getBackendCurrency(currencySymbol);
  const { currencySymbol: userCurrencySymbol, currencyCode } = useCurrency();
  const convertedAmount = useConvertedPrice(price || 0);
  const backendCurrencyCode = getCurrencyCode(backendCurrency);

  // Smart conversion: only use converted value if currencies don't match
  const finalAmount =
    backendCurrencyCode !== currencyCode ? convertedAmount : price || 0;

  // Format price with currency symbol and thousand separators
  const displayPrice = price
    ? `${userCurrencySymbol}${Math.round(finalAmount).toLocaleString()}`
    : 'Price on request';

  // Extract duration from API response, with fallback values
  const days = duration?.days?.toString() || '5';
  const nights = duration?.nights?.toString() || '4';

  return (
    <Link href={`/package-details/${slug}`}>
      <div
        className='bg-white border border-solid rounded-xl overflow-hidden cursor-pointer hover:opacity-90 transition-opacity flex flex-col w-full'
        style={{
          borderColor: strokeColors.light,
          boxShadow: '8px 8px 16px 0px rgba(0,0,0,0.05)',
          gap: spacing[7], // 28px gap between image and content
          paddingTop: spacing[3], // 12px
          paddingBottom: spacing[6], // 24px
          paddingLeft: spacing[3], // 12px
          paddingRight: spacing[3], // 12px
        }}
      >
        {/* Image with Duration Badge */}
        {displayImage && (
          <div
            className='relative rounded-lg overflow-hidden'
            style={{ height: '215px' }}
          >
            <Image
              src={displayImage}
              alt={displayTitle}
              fill
              className='object-cover'
            />

            {/* Duration Badge */}
            <div
              className='absolute top-3 right-3 flex items-center rounded-sm'
              style={{
                backgroundColor: 'rgba(0,0,0,0.1)',
                gap: spacing[1], // 4px
                padding: spacing[1], // 4px
                boxShadow: '4px 4px 4px 0px rgba(0,0,0,0.1)',
              }}
            >
              <div className='flex items-center' style={{ gap: '4px' }}>
                <div
                  className='flex flex-col items-center justify-center rounded-sm'
                  style={{
                    backgroundColor: 'rgba(241,241,239,0.19)',
                    width: '26px',
                    padding: spacing[1], // 4px
                  }}
                >
                  <span
                    className='text-center whitespace-pre-wrap'
                    style={{
                      fontFamily: fontPrimary,
                      fontWeight: 700,
                      fontSize: '14px',
                      lineHeight: 'normal',
                      color: 'white',
                    }}
                  >
                    {days}
                  </span>
                </div>
                <span
                  style={{
                    fontFamily: fontPrimary,
                    fontWeight: typography.weight.medium, // 500 for better visibility
                    fontSize: '12px', // Increased from 11px for better visibility
                    lineHeight: 'normal',
                    color: 'white',
                  }}
                >
                  Days
                </span>
              </div>

              <div
                className='rotate-[270deg]'
                style={{ height: '12px', width: '0px' }}
              >
                <div
                  style={{
                    height: '0px',
                    width: '12px',
                    borderTop: '1px solid rgba(255,255,255,0.5)',
                  }}
                />
              </div>

              <div className='flex items-center' style={{ gap: '4px' }}>
                <div
                  className='flex flex-col items-center justify-center rounded-sm'
                  style={{
                    backgroundColor: 'rgba(241,241,239,0.19)',
                    width: '26px',
                    padding: spacing[1], // 4px
                  }}
                >
                  <span
                    className='text-center whitespace-pre-wrap'
                    style={{
                      fontFamily: fontPrimary,
                      fontWeight: 700,
                      fontSize: '14px',
                      lineHeight: 'normal',
                      color: 'white',
                    }}
                  >
                    {nights}
                  </span>
                </div>
                <span
                  style={{
                    fontFamily: fontPrimary,
                    fontWeight: typography.weight.medium, // 500 for better visibility
                    fontSize: '12px', // Increased from 11px for better visibility
                    lineHeight: 'normal',
                    color: 'white',
                  }}
                >
                  Nights
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Info Container */}
        <div className='flex flex-col' style={{ gap: spacing[4] }}>
          {/* Title Container */}
          <div className='flex flex-col' style={{ gap: '11px' }}>
            <h3
              className='whitespace-pre-wrap'
              style={{
                fontFamily: fontPrimary,
                fontWeight: 700,
                fontSize: '24px',
                lineHeight: '1.2',
                color: typographyColors.primary,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                height: '57.6px', // Fixed height for exactly 2 lines (24px * 1.2 * 2)
              }}
            >
              {displayTitle}
            </h3>
            <p
              className='whitespace-pre-wrap'
              style={{
                fontFamily: fontPrimary,
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: 'normal',
                color: typographyColors.secondary,
              }}
            >
              {displayLocation}
            </p>
          </div>

          {/* Divider */}
          <div
            style={{
              height: '0px',
              borderTop: `1px solid ${strokeColors.light}`,
            }}
          />

          {/* Price Container */}
          <div className='flex items-center justify-between'>
            <div
              className='flex items-center'
              style={{ gap: spacing[1], lineHeight: 'normal' }}
            >
              <span
                style={{
                  fontFamily: fontPrimary,
                  fontWeight: 700,
                  fontSize: '24px',
                  color: typographyColors.primary,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {displayPrice}
              </span>
              {price && (
                <span
                  style={{
                    fontFamily: fontPrimary,
                    fontWeight: 400,
                    fontSize: '14px',
                    color: typographyColors.primary,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  &nbsp;/person
                </span>
              )}
            </div>

            <div
              className='flex items-center justify-center rounded-sm cursor-pointer'
              style={{
                backgroundColor: backgroundColors.alternate, // #1c1917
                gap: '8px',
                paddingLeft: spacing[4], // 16px
                paddingRight: spacing[4],
                paddingTop: spacing[2.5], // 10px
                paddingBottom: spacing[2.5],
              }}
            >
              <span
                className='text-center'
                style={{
                  fontFamily: fontPrimary,
                  fontWeight: 600,
                  fontSize: '16px',
                  lineHeight: 'normal',
                  color: typographyColors.alternate, // white
                }}
              >
                Enquire Now
              </span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
